import { CheckIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { PreVisitContactType, VisitModality } from '@hb/shared'
import { PlayIcon } from '@icons/PausePlay'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { useTimerFlush } from '../TimerFlushContext'
import { useTimerState } from '../TimerStateContext'

// ─── Format helpers ───────────────────────────────────────────────────────────

const formatMMSS = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const formatHMMSS = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ─── TimerCircleButton ────────────────────────────────────────────────────────
// Timer play/stop control — Charting UI Visual Design System (spec Section 0.6.4).
// 20×20px circle · 1.5px border · white bg · sits 5px right of the label. Play is sage
// (#3a6b47, hover #eaf3de); stop is a red square icon (▶ becomes ■ while running).

const TimerCircleButton = ({
  ariaLabel,
  variant,
  onClick,
}: {
  ariaLabel: string
  variant: 'play' | 'stop'
  onClick: () => void
}) => {
  const isStop = variant === 'stop'
  const color = isStop ? '#c0392b' : '#3a6b47'
  return (
    <Flex
      as="button"
      type="button"
      aria-label={ariaLabel}
      tabIndex={-1}
      onClick={onClick}
      flexShrink={0}
      align="center"
      justify="center"
      w="20px"
      h="20px"
      mr={2}
      borderRadius="full"
      border="1.5px solid"
      borderColor={color}
      color={color}
      bg="white"
      _dark={{ bg: 'gray.900' }}
      transition="background 0.15s ease"
      _hover={{ bg: isStop ? '#fbe9e7' : '#eaf3de' }}>
      {isStop ? (
        <Box w="7px" h="7px" bg="currentColor" borderRadius="1px" />
      ) : (
        <PlayIcon boxSize="9px" color={color} />
      )}
    </Flex>
  )
}

// ─── TimeTracker ─────────────────────────────────────────────────────────────
// Reads/writes an integer-minutes value from a react-final-form field.
// Includes a start/pause live timer for tracking time per activity.

interface TimeTrackerProps {
  fieldName: string
  label?: string
  onDelete?: () => void
}

const segmentMax: Record<'h' | 'm' | 's', number> = {
  h: 23,
  m: 59,
  s: 59,
}

const TimeTracker = ({ fieldName, label, onDelete }: TimeTrackerProps) => {
  const fieldResult = useField<number | undefined>(fieldName, {
    subscription: { value: true },
  })

  const formMinutes = fieldResult.input.value ?? 0

  const setFormMinutes = useCallback(
    (minutes: number) => {
      fieldResult.input.onChange(Math.floor(minutes))
    },
    [fieldResult.input],
  )

  const [accumulatedSeconds, setAccumulatedSeconds] = useState(() => (formMinutes ?? 0) * 60)
  const [isRunning, setIsRunning] = useState(false)
  const [liveSeconds, setLiveSeconds] = useState(0)
  const [editingSegment, setEditingSegment] = useState<'h' | 'm' | 's' | null>(null)
  const [editTotalSeconds, setEditTotalSeconds] = useState(() =>
    Math.round((formMinutes ?? 0) * 60),
  )
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hoursRef = useRef<HTMLInputElement | null>(null)
  const minutesRef = useRef<HTMLInputElement | null>(null)
  const secondsRef = useRef<HTMLInputElement | null>(null)
  // Set true when switching directly between segments so the outgoing input's
  // unmount-blur doesn't close edit mode. Reset once the switch settles.
  const suppressCloseRef = useRef(false)
  const isEditing = useMemo(() => editingSegment !== null, [editingSegment])

  useEffect(() => {
    if (editingSegment === 'h') hoursRef.current?.focus()
    else if (editingSegment === 'm') minutesRef.current?.focus()
    else if (editingSegment === 's') secondsRef.current?.focus()
    suppressCloseRef.current = false
  }, [editingSegment])

  useEffect(() => {
    if (isRunning) return
    setAccumulatedSeconds((formMinutes ?? 0) * 60)
  }, [formMinutes, isRunning])

  const { registerTimer } = useTimerFlush()
  const flushRef = useRef<() => void>(() => {})
  flushRef.current = () => {
    if (!isRunning) return
    const newTotal = accumulatedSeconds + liveSeconds
    setAccumulatedSeconds(newTotal)
    setFormMinutes(newTotal / 60)
    setLiveSeconds(0)
    setIsRunning(false)
  }
  useEffect(() => registerTimer(() => flushRef.current()), [registerTimer])

  const { setTimerRunning, stopOtherTimers, pauseFunctionsRef, startFunctionsRef } = useTimerState()
  useEffect(() => {
    pauseFunctionsRef.current[fieldName] = () => flushRef.current()
    startFunctionsRef.current[fieldName] = () => {
      stopOtherTimers(fieldName)
      setEditingSegment(null)
      setIsRunning(true)
    }
    setTimerRunning(fieldName, isRunning)
  }, [fieldName, isRunning, setTimerRunning, stopOtherTimers, pauseFunctionsRef, startFunctionsRef])

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setLiveSeconds(s => s + 1)
      }, 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isRunning])

  const handlePlayPause = useCallback(() => {
    if (isRunning) {
      const newTotal = accumulatedSeconds + liveSeconds
      setAccumulatedSeconds(newTotal)
      setFormMinutes(newTotal / 60)
      setLiveSeconds(0)
      setIsRunning(false)
    } else {
      stopOtherTimers(fieldName)
      setEditingSegment(null)
      setIsRunning(true)
    }
  }, [isRunning, liveSeconds, accumulatedSeconds, setFormMinutes, stopOtherTimers, fieldName])

  const handleSegmentClick = useCallback(
    (seg: 'h' | 'm' | 's') => {
      if (isRunning) {
        const totalSecs = accumulatedSeconds + liveSeconds
        setAccumulatedSeconds(totalSecs)
        setFormMinutes(totalSecs / 60)
        setLiveSeconds(0)
        setIsRunning(false)
        setEditTotalSeconds(Math.round(totalSecs))
      } else if (editingSegment === null) {
        setEditTotalSeconds(Math.round(accumulatedSeconds))
      }
      // Switching from one open segment to another: the currently focused input
      // will blur as it unmounts — don't let that blur close edit mode.
      if (editingSegment !== null && editingSegment !== seg) {
        suppressCloseRef.current = true
      }
      setEditingSegment(seg)
    },
    [isRunning, accumulatedSeconds, liveSeconds, editingSegment, setFormMinutes],
  )

  const handleSegmentConfirm = useCallback(() => {
    if (suppressCloseRef.current) return
    setAccumulatedSeconds(editTotalSeconds)
    setFormMinutes(editTotalSeconds / 60)
    setEditingSegment(null)
  }, [editTotalSeconds, setFormMinutes])

  const totalDisplaySeconds = accumulatedSeconds + liveSeconds
  const editH = Math.floor(editTotalSeconds / 3600)
  const editM = Math.floor((editTotalSeconds % 3600) / 60)
  const editS = editTotalSeconds % 60

  const segSep = (
    <Text
      fontSize="md"
      fontFamily="mono"
      fontWeight={500}
      height="20px"
      lineHeight={1}
      color="gray.400"
      userSelect="none">
      :
    </Text>
  )

  const makeSegInput = (
    seg: 'h' | 'm' | 's',
    ref: React.RefObject<HTMLInputElement | null>,
    val: number,
    computeTotal: (n: number) => number,
  ) => (
    <NumberInput
      size="xs"
      min={0}
      w={8}
      max={segmentMax[seg]}
      value={val}
      onChange={(_, n) => {
        const total = computeTotal(isNaN(n) ? 0 : Math.floor(n))
        setEditTotalSeconds(total)
        setFormMinutes(total / 60)
      }}>
      <NumberInputField
        ref={ref}
        px={1}
        // py={0}
        w={8}
        h="22px"
        textAlign="center"
        fontFamily="mono"
        borderRadius="md"
        _focus={{ boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
        onFocus={e => e.target.select()}
        onBlur={() => handleSegmentConfirm()}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSegmentConfirm()
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            if (seg === 'h') handleSegmentClick('m')
            else if (seg === 'm') handleSegmentClick('s')
            else handleSegmentConfirm()
          }
          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault()
            if (seg === 's') handleSegmentClick('m')
            else if (seg === 'm') handleSegmentClick('h')
          }
        }}
      />
    </NumberInput>
  )

  // ─── Label row (shared across all states) ────────────────────────────────

  const labelRow = label ? (
    <Flex align="center" gap={0.5}>
      <Text fontSize="11px" color="#3a6b47" fontWeight={500} lineHeight="1.2" whiteSpace="nowrap">
        {label}
      </Text>
      {onDelete ? (
        <Tooltip bg="red.600" color="red.50" placement="left" hasArrow label="Delete Other Time">
          <IconButton
            aria-label="Delete Other Time"
            variant="link"
            py={0.5}
            color="red.600"
            icon={<DeleteIcon />}
            _hover={{ bg: 'red.100', color: 'red.700' }}
            size="xs"
            onClick={onDelete}
          />
        </Tooltip>
      ) : null}
    </Flex>
  ) : null

  // ─── Edit mode: HH:MM:SS segment inputs ──────────────────────────────────

  if (isEditing) {
    return (
      <VStack w="auto" align="flex-start" spacing={0} pr={0.5} flexShrink={0}>
        {labelRow}
        <HStack w="100%" spacing={1} align="center">
          <HStack spacing={0.5} align="center">
            {editingSegment === 'h' ? (
              makeSegInput('h', hoursRef, editH, n => n * 3600 + editM * 60 + editS)
            ) : (
              <Text
                fontSize="sm"
                fontFamily="mono"
                fontWeight={500}
                // minW="22px"
                textAlign="center"
                height="22px"
                borderRadius="md"
                // px={0.5}
                cursor="pointer"
                tabIndex={0}
                _hover={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
                onFocus={() => handleSegmentClick('h')}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSegmentClick('h')}>
                {String(editH).padStart(2, '0')}
              </Text>
            )}
            {segSep}
            {editingSegment === 'm' ? (
              makeSegInput('m', minutesRef, editM, n => editH * 3600 + n * 60 + editS)
            ) : (
              <Text
                fontSize="sm"
                fontFamily="mono"
                fontWeight={500}
                // minW="22px"
                textAlign="center"
                borderRadius="md"
                px={0.5}
                cursor="pointer"
                _hover={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSegmentClick('m')}>
                {String(editM).padStart(2, '0')}
              </Text>
            )}
            {segSep}
            {editingSegment === 's' ? (
              makeSegInput('s', secondsRef, editS, n => editH * 3600 + editM * 60 + n)
            ) : (
              <Text
                fontSize="sm"
                fontFamily="mono"
                fontWeight={500}
                // minW="22px"
                textAlign="center"
                borderRadius="md"
                px={0.5}
                cursor="pointer"
                _hover={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSegmentClick('s')}>
                {String(editS).padStart(2, '0')}
              </Text>
            )}
          </HStack>
        </HStack>
      </VStack>
    )
  }

  // ─── Running state ────────────────────────────────────────────────────────
  // Label + red ■ stop on top; live MM:SS below in sage (#4a7c59) — spec Section 0.6.4.

  if (isRunning) {
    return (
      <Flex pr={0.5} align="center" flexShrink={0}>
        <TimerCircleButton ariaLabel="Stop timer" variant="stop" onClick={handlePlayPause} />
        <VStack w="auto" align="flex-start" spacing={0}>
          {labelRow}
          <Text color="#4a7c59" fontSize="11px" fontFamily="mono" fontWeight={500}>
            {totalDisplaySeconds >= 3600
              ? formatHMMSS(totalDisplaySeconds)
              : formatMMSS(totalDisplaySeconds)}
          </Text>
        </VStack>
      </Flex>
    )
  }

  // ─── Not started state ────────────────────────────────────────────────────
  // Label + ▶ circle only — no time is shown until the timer is started.

  // if (!hasBeenStarted) {
  //   return (
  //     <VStack w="auto" align="flex-start" spacing={0} pr={2} flexShrink={0}>
  //       <Flex align="center">
  //         {labelRow}
  //         <TimerCircleButton ariaLabel="Start timer" variant="play" onClick={handlePlayPause} />
  //       </Flex>
  //     </VStack>
  //   )
  // }

  // ─── Stopped state (has accumulated time) ────────────────────────────────
  // Label + ▶ resume on top; rounded-down "[X] min" below in muted gray (#999).

  return (
    <Flex pr={0.5} align="center" flexShrink={0}>
      <TimerCircleButton ariaLabel="Resume timer" variant="play" onClick={handlePlayPause} />
      <VStack w="auto" align="flex-start" spacing={0}>
        {labelRow}
        <Text
          fontSize="11px"
          color="#999999"
          cursor="pointer"
          _hover={{ color: '#4a7c59' }}
          onClick={() => handleSegmentClick('m')}>
          {accumulatedSeconds ? `${Math.floor(accumulatedSeconds / 60)} min` : '—'}
        </Text>
      </VStack>
    </Flex>
  )
}

// ─── useTotalMinutes ──────────────────────────────────────────────────────────

const useTotalMinutes = () => {
  const fields = [
    'faceToFaceMinutes',
    'patientHistoryMinutes',
    'labReviewMinutes',
    'externalRecordsMinutes',
    'orderingMinutes',
    'chartingMinutes',
    'counselingMinutes',
    'substanceUseCounselingMinutes',
    'careCoordinationMinutes',
    'preVisitContactMinutes',
    'otherMinutes',
  ] as const

  const values = fields.map(f => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { input } = useField<number | undefined>(`data.timeWorkLog.${f}`, {
      subscription: { value: true },
    })
    return input.value
  })

  return useMemo(
    () =>
      values
        .filter((v): v is number => typeof v === 'number')
        .reduce((sum, v) => sum + Math.round(v), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    values,
  )
}

// ─── SubstanceCounselingTimeSync ──────────────────────────────────────────────
// Headless component — keeps data.substanceUseCounseling.counselingMinutes and
// data.timeWorkLog.substanceUseCounselingMinutes in sync bidirectionally.
// Uses prevRef guards to detect which side changed and avoid infinite loops.

const SubstanceCounselingTimeSync = () => {
  const form = useForm()
  const {
    input: { value: substanceMinutes },
  } = useField<number | undefined>('data.substanceUseCounseling.counselingMinutes', {
    subscription: { value: true },
  })
  const {
    input: { value: logMinutes },
  } = useField<number | undefined>('data.timeWorkLog.substanceUseCounselingMinutes', {
    subscription: { value: true },
  })
  const prevSubstanceRef = useRef<number | undefined>(undefined)
  const prevLogRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (substanceMinutes !== prevSubstanceRef.current && substanceMinutes !== logMinutes) {
      form.change('data.timeWorkLog.substanceUseCounselingMinutes', substanceMinutes)
    }
    if (logMinutes !== prevLogRef.current && logMinutes !== substanceMinutes) {
      form.change('data.substanceUseCounseling.counselingMinutes', logMinutes)
    }
    prevSubstanceRef.current = substanceMinutes
    prevLogRef.current = logMinutes
  }, [substanceMinutes, logMinutes, form])

  return null
}

// ─── ElapsedTimeInput (exported) ──────────────────────────────────────────────

export const ElapsedTimeInput = () => {
  const { showOtherTime: showOther, setShowOtherTime: setShowOther } = useTimerState()

  // When the provider taps "+ Other", the whole row is replaced by an inline
  // input for naming the new time entry rather than opening the Time & Work Log.
  const [isNamingOther, setIsNamingOther] = useState(false)
  const [otherDraft, setOtherDraft] = useState('')
  const otherInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isNamingOther) otherInputRef.current?.focus()
  }, [isNamingOther])

  const { input: modalityInput } = useField<VisitModality | undefined>(
    'data.visitLocation.modality',
    { subscription: { value: true } },
  )
  const { input: externalRecordsInput } = useField<boolean | undefined>(
    'data.externalRecordsReviewed',
    { subscription: { value: true } },
  )
  const { input: checklistInput } = useField<{ items: Array<{ checked: boolean }> } | undefined>(
    'data.patientEducationChecklist',
    { subscription: { value: true } },
  )
  const { input: referralsInput } = useField<string[] | undefined>('data.referrals', {
    subscription: { value: true },
  })
  const { input: labReviewConfirmationInput } = useField<unknown>(
    'data.labs.labReviewConfirmation',
    { subscription: { value: true } },
  )
  const { input: preVisitContactTypeInput } = useField<PreVisitContactType | undefined>(
    'data.preVisitContact.contactType',
    { subscription: { value: true } },
  )
  const { input: substanceCounselingProvidedInput } = useField<boolean | undefined>(
    'data.substanceUseCounseling.counselingProvided',
    { subscription: { value: true } },
  )

  const isAudioOnly = modalityInput.value === 'telehealth-audio-only'
  const hasLabReview = !!labReviewConfirmationInput.value
  const hasExternalRecords = externalRecordsInput.value === true
  const hasPatientEducation = checklistInput.value?.items?.some(i => i.checked) === true
  const hasReferral = Array.isArray(referralsInput.value) && referralsInput.value.length > 0
  const hasPreVisitContact =
    preVisitContactTypeInput.value != null && preVisitContactTypeInput.value !== 'in-person-only'
  const hasSubstanceCounseling = substanceCounselingProvidedInput.value === true

  const total = useTotalMinutes()
  const form = useForm()

  useEffect(() => {
    form.change('data.timeWorkLog.totalMinutes', total)
  }, [total, form])

  const { input: otherLabelInput } = useField<string | undefined>('data.timeWorkLog.otherLabel', {
    subscription: { value: true },
  })
  const otherLabel = otherLabelInput.value || 'Other'

  const handleDeleteOther = useCallback(() => {
    setShowOther(false)
    form.change('data.timeWorkLog.otherMinutes', undefined)
    form.change('data.timeWorkLog.otherLabel', undefined)
  }, [form, setShowOther])

  const handleStartNamingOther = useCallback(() => {
    setOtherDraft('')
    setIsNamingOther(true)
  }, [])

  const handleCancelNamingOther = useCallback(() => {
    setIsNamingOther(false)
    setOtherDraft('')
  }, [])

  const handleConfirmNamingOther = useCallback(() => {
    const trimmed = otherDraft.trim()
    if (!trimmed) return
    form.change('data.timeWorkLog.otherLabel', trimmed)
    setShowOther(true)
    setIsNamingOther(false)
    setOtherDraft('')
  }, [otherDraft, form, setShowOther])

  // ─── Naming mode: the entire row is replaced by a title input ────────────
  if (isNamingOther) {
    return (
      <HStack
        bg="#e8f3ec"
        _dark={{ bg: 'green.900', borderColor: 'green.700' }}
        w="100%"
        align="center"
        px={3}
        py={1}
        flexShrink={0}>
        <Input
          ref={otherInputRef}
          size="xs"
          maxW="350px"
          bg="white"
          _dark={{ bg: 'gray.800' }}
          borderRadius={6}
          placeholder="Name this time entry..."
          value={otherDraft}
          onChange={e => setOtherDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleConfirmNamingOther()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              handleCancelNamingOther()
            }
          }}
        />
        <Tooltip placement="top" hasArrow label="Confirm">
          <IconButton
            aria-label="Confirm time entry name"
            size="sm"
            variant="ghost"
            colorScheme="green"
            isDisabled={!otherDraft.trim()}
            icon={<CheckIcon />}
            onClick={handleConfirmNamingOther}
          />
        </Tooltip>
        <Tooltip placement="top" hasArrow label="Cancel">
          <IconButton
            aria-label="Cancel time entry name"
            size="sm"
            variant="ghost"
            colorScheme="gray"
            icon={<CloseIcon boxSize={2.5} />}
            onClick={handleCancelNamingOther}
          />
        </Tooltip>
      </HStack>
    )
  }

  return (
    <Box w="100%">
      <HStack
        bg="#e8f3ec"
        _dark={{ bg: 'green.900' }}
        w="100%"
        align="center"
        spacing={0}
        // Timer bar padding — 8px vertical / 20px horizontal per spec Section 0.6.4.
        flexShrink={0}>
        {/* Trackers scroll horizontally on narrow screens; the total stays pinned on the right. */}
        <Box flex={1} minW={0} position="relative">
          <HStack
            align="center"
            spacing={2.5}
            overflowX="auto"
            py={2}
            divider={<StackDivider />}
            pl={3}
            pr={3}
            sx={{ scrollbarWidth: 'thin' }}>
            {!isAudioOnly && (
              <TimeTracker fieldName="data.timeWorkLog.faceToFaceMinutes" label="Face-to-Face" />
            )}
            {hasPreVisitContact && (
              <TimeTracker fieldName="data.timeWorkLog.preVisitContactMinutes" label="Pre-Visit" />
            )}
            <TimeTracker fieldName="data.timeWorkLog.patientHistoryMinutes" label="Pt. History" />
            <TimeTracker fieldName="data.timeWorkLog.chartingMinutes" label="Charting" />
            {hasLabReview && (
              <TimeTracker fieldName="data.timeWorkLog.labReviewMinutes" label="Lab Review" />
            )}
            {hasExternalRecords && (
              <TimeTracker
                fieldName="data.timeWorkLog.externalRecordsMinutes"
                label="Ext. Records"
              />
            )}
            {hasReferral && (
              <TimeTracker fieldName="data.timeWorkLog.orderingMinutes" label="Ordering" />
            )}
            {hasPatientEducation && (
              <TimeTracker fieldName="data.timeWorkLog.counselingMinutes" label="Counseling" />
            )}
            {hasSubstanceCounseling && (
              <TimeTracker
                fieldName="data.timeWorkLog.substanceUseCounselingMinutes"
                label="Subst. Use Counseling"
              />
            )}
            {hasReferral ? (
              <TimeTracker
                fieldName="data.timeWorkLog.careCoordinationMinutes"
                label="Care Coord."
              />
            ) : null}
            {showOther ? (
              <HStack spacing={0} align="flex-end" flexShrink={0}>
                <TimeTracker
                  onDelete={handleDeleteOther}
                  fieldName="data.timeWorkLog.otherMinutes"
                  label={otherLabel}
                />
              </HStack>
            ) : (
              <Button
                variant="link"
                size="sm"
                fontSize="11px"
                fontWeight={500}
                color="#4a7c59"
                flexShrink={0}
                whiteSpace="nowrap"
                _hover={{ textDecoration: 'underline' }}
                onClick={handleStartNamingOther}>
                + Other
              </Button>
            )}
          </HStack>
          {/* Fade the right edge to hint at more horizontally-scrollable trackers. */}
          <Box
            aria-hidden
            pointerEvents="none"
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            w="28px"
            bgGradient="linear(to-r, rgba(232, 243, 236, 0), #e8f3ec)"
            _dark={{ bgGradient: 'linear(to-r, transparent, var(--chakra-colors-green-900))' }}
          />
        </Box>
        <Flex align="center" gap={1.5} flexShrink={0} pl={11} pr={2}>
          <Text color="#3a6b47" fontSize="11px" fontWeight={500}>
            Total:
          </Text>
          <Text
            color="#3a6b47"
            fontSize="11px"
            fontWeight={500}
            whiteSpace="nowrap"
            _dark={{ color: 'green.200' }}>
            {total} min
          </Text>
        </Flex>
      </HStack>
      <SubstanceCounselingTimeSync />
    </Box>
  )
}
